import { ApolloClient } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  CreateAudienceInterestCheckoutMutation,
  CreateAudienceInterestCheckoutMutationVariables,
  GetAudienceInterestEligibleQuery,
  GetAudienceInterestFilmQuery,
  UpdateAudienceInterestTicketsMutation,
  UpdateAudienceInterestTicketsMutationVariables,
  UpdateAudienceInterestUserMutation,
  UpdateAudienceInterestUserMutationVariables,
  UpsertAudienceInterestWatchMutation,
  UpsertAudienceInterestWatchMutationVariables,
} from '@/types/codegen-federation'
import { hasAuthJwtCookie } from '@/utils/auth-utils'
import {
  CREATE_AUDIENCE_INTEREST_CHECKOUT,
  GET_AUDIENCE_INTEREST_ELIGIBLE,
  GET_AUDIENCE_INTEREST_FILM,
  UPDATE_AUDIENCE_INTEREST_TICKETS,
  UPDATE_AUDIENCE_INTEREST_USER,
  UPSERT_AUDIENCE_INTEREST_WATCH,
} from './queries'

export const useAudienceInterestEligible = () => {
  return useQuery<GetAudienceInterestEligibleQuery>(GET_AUDIENCE_INTEREST_ELIGIBLE, {
    skip: !hasAuthJwtCookie(),
  })
}

export const getAudienceInterestFilm = async (client: ApolloClient) => {
  const resp = await client.query<GetAudienceInterestFilmQuery>({
    query: GET_AUDIENCE_INTEREST_FILM,
  })

  return {
    data: resp?.data || null,
    error: resp?.data?.userAudienceInterestFilm?.message || resp?.error?.message || null,
  }
}

export const getAudienceInterestCheckout = async (client: ApolloClient, email: string) => {
  if (!email) {
    return {
      data: null,
      error: 'No email provided',
    }
  }

  try {
    const resp = await client.mutate<
      CreateAudienceInterestCheckoutMutation,
      CreateAudienceInterestCheckoutMutationVariables
    >({
      mutation: CREATE_AUDIENCE_INTEREST_CHECKOUT,
      variables: {
        input: {
          email,
        },
      },
    })

    const errorMessage = resp?.data?.createAudienceInterestCheckout?.message || resp?.error?.message || null

    return {
      data: resp?.data || null,
      error: errorMessage,
    }
  } catch (error) {
    return {
      data: null,
      error: null,
    }
  }
}

export const useUpsertAudienceInterestWatch = () => {
  const [upsertAudienceInterestWatch, { loading, data, error }] = useMutation<
    UpsertAudienceInterestWatchMutation,
    UpsertAudienceInterestWatchMutationVariables
  >(UPSERT_AUDIENCE_INTEREST_WATCH)

  return {
    upsertAudienceInterestWatch,
    loading,
    data,
    error,
  }
}

export const useUpsertAudienceInterestAgeGroup = () => {
  const [upsertAudienceInterestAgeGroup, { loading, data, error }] = useMutation<
    UpdateAudienceInterestUserMutation,
    UpdateAudienceInterestUserMutationVariables
  >(UPDATE_AUDIENCE_INTEREST_USER)

  return {
    upsertAudienceInterestAgeGroup,
    loading,
    data,
    error,
  }
}

export const useUpdateAudienceInterestTickets = () => {
  const [updateAudienceInterestTickets, { loading, data, error }] = useMutation<
    UpdateAudienceInterestTicketsMutation,
    UpdateAudienceInterestTicketsMutationVariables
  >(UPDATE_AUDIENCE_INTEREST_TICKETS)

  return {
    updateAudienceInterestTickets,
    loading,
    data,
    error,
  }
}
